import React, {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  Anchor,
  Box,
  Button,
  TextInput,
  ResponsiveContext,
  Form,
  FormField,
  FormExtendedEvent,
  Spinner,
  Text,
} from 'grommet';
import { Search } from 'grommet-icons';
import { ThemeModeContext } from '@pentester.com/grommet-theme';
import { ButtonExtendedProps } from 'grommet/components/Button';
import { BoxExtendedProps } from 'grommet/components/Box';
import { useMutation } from '@/hooks/graphql';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { trackScanClick } from '../../utils/tracking';

interface SubmitButtonProps extends ButtonExtendedProps {
  loading?: boolean;
}

const SubmitButton: FC<SubmitButtonProps> = ({ loading, ...rest }) => {
  const { mode } = useContext(ThemeModeContext);
  return (
    <Button
      primary
      label="Scan"
      color={mode === `light` ? `brand` : `status-error`}
      icon={
        loading ? (
          <Spinner color={mode === `light` ? `status-error` : `brand`} />
        ) : (
          <Search />
        )
      }
      type="submit"
      {...rest}
    />
  );
};
SubmitButton.defaultProps = {
  loading: false,
};

const MUTATION = `
  mutation ($url: String!) {
    validateUrl(
      input: {
        url: $url
      }
    ) {
      ... on StatusType {
        status
      }
      ... on OperationInfo {
        messages {
          message
          field
          kind
        }
      }
    }
  }
`;

const VALIDATE_EMAIL_MUTATION = `
  mutation ($email: String!) {
    validateEmail(
      input: {
        email: $email
      }
    ) {
      ... on StatusType {
        status
      }
      ... on OperationInfo {
        messages {
          message
          field
          kind
        }
      }
    }
  }
`;

const removeTrailingSlash = (url: string) =>
  url.endsWith(`/`) ? url.slice(0, -1) : url;
const addProtocol = (url: string): string => {
  const protocol = url.match(/(^\w+:|^)\/\//);
  if (
    protocol &&
    protocol.length > 0 &&
    [`http://`, `https://`].includes(protocol[0])
  ) {
    return url;
  }
  return `https://${url}`;
};

const REGEX_VALID_URL =
  /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})$/;
const REGEX_VALID_EMAIL =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface Props {
  buttonProps?: ButtonExtendedProps;
  boxProps?: BoxExtendedProps;
  containerProps?: BoxExtendedProps;
  inline?: boolean;
  header: string;
}

type FormFields = {
  target: string;
};

export const ScanTarget: FC<Props> = ({
  buttonProps,
  boxProps,
  containerProps,
  header,
  inline = true,
}): ReactElement => {
  const [value, setValue] = useState<FormFields>({
    target: ``,
  });
  const targetType: 'email' | 'url' = value.target.includes(`@`)
    ? `email`
    : `url`;
  const size = useContext(ResponsiveContext);
  const [error, setError] = useState<string | undefined>();
  const twoRows = useMemo(() => !inline || size === `small`, [inline, size]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [submitForm, { loading }] = useMutation(MUTATION);
  const [validateEmail, { loading: loadingEmailValidation }] = useMutation(
    VALIDATE_EMAIL_MUTATION,
  );

  const handleSubmit = useCallback(
    async (event: FormExtendedEvent<FormFields>) => {
      setError(undefined);
      let target = event.value.target.toLowerCase();

      if (targetType === `url`) {
        target = removeTrailingSlash(target);
        target = addProtocol(target);
        const data = await submitForm({ url: target });
        if (data?.validateUrl?.messages?.length > 0) {
          setError(data.validateUrl.messages[0].message);
          return;
        }
      } else if (targetType === `email`) {
        const data = await validateEmail({ email: target });
        if (data?.validateEmail?.messages?.length > 0) {
          setError(data.validateEmail.messages[0].message);
          return;
        }
      }

      trackScanClick(targetType === `email` ? `email` : `website`, header);

      const recaptcha = await executeRecaptcha(`scan`);
      window.location.assign(
        `${process.env.GATSBY_DASHBOARD_URL}/anonymous?target=${target}&type=${targetType}&recaptcha=${recaptcha}`,
      );
    },
    [executeRecaptcha, submitForm, targetType],
  );

  return (
    <Box fill="horizontal">
      <Form<FormFields>
        value={value}
        onChange={(nextValue) =>
          setValue({ target: nextValue?.target?.trim() })
        }
        onReset={() => setValue({ target: `` })}
        onSubmit={handleSubmit}
        messages={{ required: `Required`, invalid: `Invalid` }}
      >
        <Box {...containerProps}>
          <Box
            alignSelf="center"
            background={{ light: `status-error`, dark: `dark-3` }}
            round="xsmall"
            pad="medium"
            align="center"
            elevation="small"
            fill={size === `small` ? `horizontal` : false}
            {...boxProps}
          >
            <Box
              pad="xsmall"
              round="xsmall"
              elevation="xsmall"
              background="light-1"
              direction="row"
              align="center"
              fill="horizontal"
            >
              <FormField
                name="target"
                required={{ indicator: false }}
                error={error}
                contentProps={{ border: false, fill: `horizontal` }}
                width="100%"
                validate={
                  targetType === `url`
                    ? [
                        {
                          regexp: REGEX_VALID_URL,
                          message: `Invalid URL`,
                        },
                      ]
                    : [
                        {
                          regexp: REGEX_VALID_EMAIL,
                          message: `Invalid email`,
                        },
                      ]
                }
              >
                <TextInput
                  placeholder="Enter email or website"
                  name="target"
                  autoCapitalize="none"
                  type="text"
                />
              </FormField>
              {!twoRows && (
                <SubmitButton
                  loading={loading || loadingEmailValidation}
                  {...buttonProps}
                />
              )}
            </Box>
          </Box>
          {twoRows && (
            <SubmitButton
              loading={loading || loadingEmailValidation}
              margin={{ top: `small` }}
              fill="horizontal"
              {...buttonProps}
            />
          )}
        </Box>
      </Form>
      <Box align="center" margin={{ top: `small` }}>
        <Text size="xsmall" color={{ light: `dark-4`, dark: `light-1` }}>
          By conducting scans you agree to our{` `}
          <Anchor
            size="xsmall"
            color={{ light: `dark-4`, dark: `light-1` }}
            href="https://pentester.com/terms-and-conditions/"
            label="Terms"
          />
        </Text>
      </Box>
    </Box>
  );
};

ScanTarget.defaultProps = {
  buttonProps: {},
  boxProps: {},
  containerProps: {},
  inline: true,
};
